import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaChartBar } from 'react-icons/fa';
import { getAuditLogStatistics } from '../../../redux/Slices/AuditLogSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';

const AuditLogStatistics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTitle('auditLogs.statisticsTitle', { defaultValue: 'إحصائيات التحركات' });
    fetchStatistics();
  }, [setTitle]);

  const fetchStatistics = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(getAuditLogStatistics(i18n.language));
      if (getAuditLogStatistics.fulfilled.match(result)) {
        const data = result.payload?.data || result.payload;
        setStatistics(data);
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchStatisticsError', { defaultValue: 'فشل في جلب الإحصائيات' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.fetchStatisticsError', { defaultValue: 'فشل في جلب الإحصائيات' }), { rtl: isRTL });
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return parseFloat(num).toLocaleString('en-US');
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
        {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            {t('auditLogs.statisticsTitle', { defaultValue: 'إحصائيات التحركات' })}
          </h2>
          <button
            onClick={() => navigate('/audit-logs')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft /> {t('auditLogs.back', { defaultValue: 'رجوع' })}
          </button>
        </div>

        {statistics && (
          <>
            {/* Total Logs */}
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#999', marginBottom: '15px', fontSize: '14px', fontWeight: 'normal' }}>
                {t('auditLogs.totalLogs', { defaultValue: 'إجمالي السجلات' })}
              </h3>
              <div style={{ color: '#AC2000', fontSize: '48px', fontWeight: 'bold' }}>
                {formatNumber(statistics.total_logs || 0)}
              </div>
            </div>

            {/* Total by Event */}
            {statistics.total_by_event && (
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#999', marginBottom: '15px', fontSize: '14px', fontWeight: 'normal' }}>
                  {t('auditLogs.totalByEvent', { defaultValue: 'إجمالي حسب نوع الحدث' })}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {Object.entries(statistics.total_by_event).map(([event, count]) => (
                    <div key={event} style={{
                      backgroundColor: '#14263b',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '8px' }}>
                        {t('auditLogs.events.' + event, { defaultValue: event })}
                      </div>
                      <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                        {formatNumber(count)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total by Model */}
            {statistics.total_by_model && (
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#999', marginBottom: '15px', fontSize: '14px', fontWeight: 'normal' }}>
                  {t('auditLogs.totalByModel', { defaultValue: 'إجمالي حسب نوع النموذج' })}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {Object.entries(statistics.total_by_model).map(([model, count]) => (
                    <div key={model} style={{
                      backgroundColor: '#14263b',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #333'
                    }}>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '8px', wordBreak: 'break-word' }}>
                        {model.replace(/App\\Models\\/g, '')}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                        {formatNumber(count)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Most Active Users */}
            {statistics.most_active_users && Array.isArray(statistics.most_active_users) && statistics.most_active_users.length > 0 && (
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#999', marginBottom: '15px', fontSize: '14px', fontWeight: 'normal' }}>
                  {t('auditLogs.mostActiveUsers', { defaultValue: 'أكثر المستخدمين نشاطاً' })}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#AC2000' }}>
                        <th style={{ padding: '10px', textAlign: 'center', color: 'white', border: '1px solid #333', fontSize: '12px' }}>
                          {t('auditLogs.user', { defaultValue: 'المستخدم' })}
                        </th>
                        <th style={{ padding: '10px', textAlign: 'center', color: 'white', border: '1px solid #333', fontSize: '12px' }}>
                          {t('auditLogs.count', { defaultValue: 'عدد السجلات' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.most_active_users.map((user, idx) => (
                        <tr key={user.user_id || idx} style={{ borderBottom: '1px solid #333' }}>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#ddd', border: '1px solid #333', fontSize: '12px' }}>
                            {user.user_name || `User #${user.user_id}`}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#ddd', border: '1px solid #333', fontSize: '12px' }}>
                            {formatNumber(user.count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {statistics.recent_activity && Array.isArray(statistics.recent_activity) && statistics.recent_activity.length > 0 && (
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#999', marginBottom: '15px', fontSize: '14px', fontWeight: 'normal' }}>
                  {t('auditLogs.recentActivity', { defaultValue: 'النشاط الأخير' })}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {statistics.recent_activity.map((activity, idx) => (
                    <div key={idx} style={{
                      backgroundColor: '#14263b',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '8px' }}>
                        {new Date(activity.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </div>     
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                        {formatNumber(activity.count)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
           
export default AuditLogStatistics;
